// openEHR RM model — BMM package org.openehr.rm.data_types.quantity.date_time
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { DV_ABSOLUTE_QUANTITY, DV_AMOUNT } from "../quantity.ts";
import type { DV_ORDERED, DV_QUANTIFIED } from "../quantity.ts";
import * as openehr_base from "../../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../../base/apply_rm_init.ts";

/**
 * Represents a period of time with respect to a notional point in time, which is not specified. A sign may be used to indicate the duration is  backwards  in time rather than forwards.
 *
 * NOTE: two deviations from ISO 8601 are supported, the first, to allow a negative sign, and the second allowing the 'W' designator to be mixed with other designators. See time types section in the Foundation Types model.
 *
 * Used for recording the duration of something in the real world, particularly when there is a need a) to represent the duration in customary format, i.e. days, hours, minutes etc, and b) if it will be used in computational operations with date/time quantities, i.e. additions, subtractions etc.
 *
 * Misuse: Durations cannot be used to represent points in time, or intervals of time.
 */
export class DV_DURATION extends DV_AMOUNT {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * ISO8601 duration string, including described deviations to support negative values and weeks.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * Sum of this Duration and \`_other_\`.
   * Uses the Iso8601_duration.add() method from BASE package.
   * @param other - Parameter
   * @returns Result value
   */
  override add(other: DV_DURATION): DV_DURATION {
    // Create Iso8601_duration instances from the values
    const dur1 = new openehr_base.Iso8601_duration();
    dur1.value = this.value || "";
    const dur2 = new openehr_base.Iso8601_duration();
    dur2.value = other.value || "";
    
    // Use BASE package add method
    const resultDur = dur1.add(dur2);
    
    // Create and return new DV_DURATION with result
    const result = new DV_DURATION();
    result.value = resultDur.value;
    return result;
  }

  /**
   * Difference of this Duration and \`_other_\`.
   * Uses the Iso8601_duration.subtract() method from BASE package.
   * @param other - Parameter
   * @returns Result value
   */
  override subtract(other: DV_DURATION): DV_DURATION {
    // Create Iso8601_duration instances from the values
    const dur1 = new openehr_base.Iso8601_duration();
    dur1.value = this.value || "";
    const dur2 = new openehr_base.Iso8601_duration();
    dur2.value = other.value || "";
    
    // Use BASE package subtract method
    const resultDur = dur1.subtract(dur2);
    
    // Create and return new DV_DURATION with result
    const result = new DV_DURATION();
    result.value = resultDur.value;
    return result;
  }

  /**
   * Product of this Duration and \`_factor_\`.
   * Uses the Iso8601_duration.multiply() method from BASE package.
   * @param factor - Parameter
   * @returns Result value
   */
  override multiply(factor: number): DV_DURATION {
    // Create Iso8601_duration instance from the value
    const dur = new openehr_base.Iso8601_duration();
    dur.value = this.value || "";
    
    // Use BASE package multiply method
    const resultDur = dur.multiply(factor);
    
    // Create and return new DV_DURATION with result
    const result = new DV_DURATION();
    result.value = resultDur.value;
    return result;
  }

  /**
   * True if this duration object is less than \`_other_\`, based on comparison of \`_magnitude()_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_DURATION): openehr_base.Boolean {
    // Compare based on magnitude (seconds)
    const a = this.ordered_magnitude().value ?? 0;
    const b = other.ordered_magnitude().value ?? 0;
    return openehr_base.Boolean.from(a < b);
  }

  /**
   * True, for any two Durations.
   * Per openEHR specification, any two DV_DURATION instances are strictly comparable
   * because they can both be converted to a common unit (seconds) for comparison.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_DURATION): openehr_base.Boolean {
    // Per openEHR specification: "True, for any two Durations"
    // All durations can be compared via magnitude (seconds)
    return openehr_base.Boolean.from(other instanceof DV_DURATION);
  }

  /**
   * Negated version of current duration.
   *
   * Assuming the current duration is positive, the negated version represents a time prior to some origin point, or a negative age (e.g. so-called 'adjusted age' of premature infant).
   * @returns Result value
   */
  override negative(): DV_DURATION {
    // Negate the duration according to ISO8601 format
    // ISO8601 durations: P[n]Y[n]M[n]DT[n]H[n]M[n]S
    // Negative durations: -P[n]Y[n]M[n]DT[n]H[n]M[n]S
    const val = this.value || "";
    const result = new DV_DURATION();
    
    if (val.startsWith("-P")) {
      // Already negative, remove the "-" to make positive
      result.value = val.substring(1);
    } else if (val.startsWith("P")) {
      // Positive, prepend "-" to make negative
      result.value = "-" + val;
    } else if (val.startsWith("-")) {
      // Non-standard negative format, remove the "-"
      result.value = val.substring(1);
    } else {
      // Handle edge case: add P if missing
      result.value = "-P" + val;
    }
    return result;
  }

  /**
   * Numeric value of the duration as a number of seconds. Computed using the method \`_to_seconds()_\` inherited from \`Iso8601_duration\`.
   * @returns Result value
   */
  override ordered_magnitude(): openehr_base.Real {
    // Use the Iso8601_duration.to_seconds() method from BASE package
    const dur = new openehr_base.Iso8601_duration();
    dur.value = this.value || "";
    return openehr_base.Real.from(dur.to_seconds());
  }

  /**
   * Value equality comparison.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_DURATION)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare value strings
    return openehr_base.Boolean.from(this.value === other.value);
  }
}

/**
 * Specialised temporal variant of \`DV_ABSOLUTE_QUANTITY\` whose diff type is \`DV_DURATION\`.
 */
export abstract class DV_TEMPORAL extends DV_ABSOLUTE_QUANTITY {
  /**
   * Addition of a Duration to this temporal entity.
   * @param a_diff - Parameter
   * @returns Result value
   */
  abstract override add(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY;

  /**
   * Subtract a Duration from this temporal entity.
   * @param a_diff - Parameter
   * @returns Result value
   */
  abstract override subtract(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY;

  /**
   * Difference between this temporal entity and \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  abstract override diff(other: DV_ABSOLUTE_QUANTITY): DV_AMOUNT;
}

/**
 * Represents an absolute point in time, as measured on the Gregorian calendar, and specified only to the day. Semantics defined by ISO 8601. Used for recording dates in real world time. The partial form is used for approximate birth dates, dates of death, etc.
 */
export class DV_DATE extends DV_TEMPORAL {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * ISO8601 date string.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * Numeric value of the date as days since the calendar origin date \`0001-01-01\`.
   * @returns Result value
   */
  override ordered_magnitude(): openehr_base.Integer {
    // Calculate days since 0001-01-01
    const val = this.value || "";
    if (!val) {
      return openehr_base.Integer.from(0);
    }
    try {
      // Parse the date using Iso8601_date from BASE
      const date = new openehr_base.Iso8601_date();
      date.value = val;
      
      // Get year, month, day components
      const year = date.year().value;
      const month = date.month().value || 1;
      const day = date.day().value || 1;
      
      // Calculate days since 0001-01-01 using Julian day calculation
      // This is an approximation using the Gregorian calendar formula
      const a = Math.floor((14 - month) / 12);
      const y = (year ?? 0) + 4800 - a;
      const m = month + 12 * a - 3;
      
      // Julian Day Number formula for Gregorian calendar
      const jdn = day + Math.floor((153 * m + 2) / 5) + 365 * y +
                  Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
      
      // Origin 0001-01-01 has JDN = 1721426
      const daysSinceOrigin = jdn - 1721426;
      
      return openehr_base.Integer.from(daysSinceOrigin);
    } catch {
      return openehr_base.Integer.from(0);
    }
  }

  /**
   * Addition of a Duration to this Date.
   * Uses the Iso8601_date.add() method from BASE package.
   * @param a_diff - Parameter
   * @returns Result value
   */
  override add(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY {
    if (!(a_diff instanceof DV_DURATION)) {
      throw new Error("DV_DATE.add requires DV_DURATION");
    }
    const date = new openehr_base.Iso8601_date();
    date.value = this.value || "";
    const dur = new openehr_base.Iso8601_duration();
    dur.value = a_diff.value || "";

    const resultDate = date.add(dur);

    const result = new DV_DATE();
    result.value = resultDate.value;
    return result;
  }

  /**
   * Subtract a Duration from this Date.
   * Uses the Iso8601_date.subtract() method from BASE package.
   * @param a_diff - Parameter
   * @returns Result value
   */
  override subtract(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY {
    if (!(a_diff instanceof DV_DURATION)) {
      throw new Error("DV_DATE.subtract requires DV_DURATION");
    }
    const date = new openehr_base.Iso8601_date();
    date.value = this.value || "";
    const dur = new openehr_base.Iso8601_duration();
    dur.value = a_diff.value || "";

    const resultDate = date.subtract(dur);

    const result = new DV_DATE();
    result.value = resultDate.value;
    return result;
  }

  /**
   * Difference between this Date and \`_other_\`.
   * Uses the Iso8601_date.diff() method from BASE package.
   * @param other - Parameter
   * @returns Result value
   */
  override diff(other: DV_ABSOLUTE_QUANTITY): DV_AMOUNT {
    if (!(other instanceof DV_DATE)) {
      throw new Error("DV_DATE.diff requires another DV_DATE");
    }
    const date1 = new openehr_base.Iso8601_date();
    date1.value = this.value || "";
    const date2 = new openehr_base.Iso8601_date();
    date2.value = other.value || "";
    
    // Use BASE package diff method
    const resultDur = date1.diff(date2);
    
    // Create and return new DV_DURATION with result
    const result = new DV_DURATION();
    result.value = resultDur.value;
    return result;
  }

  /**
   * True if this date object is less than \`_other_\`, based on comparison of \`_magnitude()_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_DATE)) {
      return openehr_base.Boolean.from(false);
    }
    const otherDate = other;
    // Compare ISO8601 date strings lexicographically (works for standard format)
    if (!this.value || !other.value) {
      return openehr_base.Boolean.from(false);
    }
    return openehr_base.Boolean.from(this.value < other.value);
  }

  /**
   * True, for any two Dates.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    // Dates are strictly comparable with other dates
    return openehr_base.Boolean.from(other instanceof DV_DATE);
  }

  /**
   * Value equality comparison.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_DATE)) {
      return openehr_base.Boolean.from(false);
    }
    // Basic implementation: compare values
    return openehr_base.Boolean.from(this.value === other.value);
  }
}

/**
 * Represents an absolute point in time from an origin usually interpreted as meaning the start of the current day, specified to a fraction of a second. Semantics defined by ISO 8601.
 *
 * Used for recording real world times, rather than scientifically measured fine amounts of time. The partial form is used for approximate times of events and substance administrations.
 */
export class DV_TIME extends DV_TEMPORAL {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * ISO8601 time string
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * Numeric value of the time as seconds since the start of day, i.e. \`00:00:00\`.
   * @returns Result value
   */
  override ordered_magnitude(): openehr_base.Real {
    // Calculate seconds since 00:00:00
    const val = this.value || "";
    if (!val) {
      return openehr_base.Real.from(0);
    }
    try {
      const time = new openehr_base.Iso8601_time();
      time.value = val;
      const hours = time.hour().value ?? 0;
      const minutes = time.minute().value ?? 0;
      const seconds = time.second().value ?? 0;
      const fractional = time.fractional_second() ?? 0;
      return openehr_base.Real.from(
        hours * 3600 + minutes * 60 + seconds + fractional,
      );
    } catch {
      return openehr_base.Real.from(0);
    }
  }

  /**
   * Addition of a Duration to this Time.
   * Uses the Iso8601_time.add() method from BASE package.
   * @param a_diff - Parameter
   * @returns Result value
   */
  override add(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY {
    if (!(a_diff instanceof DV_DURATION)) {
      throw new Error("DV_TIME.add requires DV_DURATION");
    }
    const time = new openehr_base.Iso8601_time();
    time.value = this.value || "";
    const dur = new openehr_base.Iso8601_duration();
    dur.value = a_diff.value || "";

    const resultTime = time.add(dur);

    const result = new DV_TIME();
    result.value = resultTime.value;
    return result;
  }

  /**
   * Subtract a Duration from this Time.
   * Uses the Iso8601_time.subtract() method from BASE package.
   * @param a_diff - Parameter
   * @returns Result value
   */
  override subtract(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY {
    if (!(a_diff instanceof DV_DURATION)) {
      throw new Error("DV_TIME.subtract requires DV_DURATION");
    }
    const time = new openehr_base.Iso8601_time();
    time.value = this.value || "";
    const dur = new openehr_base.Iso8601_duration();
    dur.value = a_diff.value || "";

    const resultTime = time.subtract(dur);

    const result = new DV_TIME();
    result.value = resultTime.value;
    return result;
  }

  /**
   * Difference between this Time and \`_other_\`.
   * Uses the Iso8601_time.diff() method from BASE package.
   * @param other - Parameter
   * @returns Result value
   */
  override diff(other: DV_ABSOLUTE_QUANTITY): DV_AMOUNT {
    if (!(other instanceof DV_TIME)) {
      throw new Error("DV_TIME.diff requires another DV_TIME");
    }
    const time1 = new openehr_base.Iso8601_time();
    time1.value = this.value || "";
    const time2 = new openehr_base.Iso8601_time();
    time2.value = other.value || "";
    
    // Use BASE package diff method
    const resultDur = time1.diff(time2);
    
    // Create and return new DV_DURATION with result
    const result = new DV_DURATION();
    result.value = resultDur.value;
    return result;
  }

  /**
   * True if this time object is less than \`_other_\`, based on comparison of \`_magnitude()_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_TIME)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare ISO8601 time strings lexicographically (works for standard format)
    if (!this.value || !other.value) {
      return openehr_base.Boolean.from(false);
    }
    return openehr_base.Boolean.from(this.value < other.value);
  }

  /**
   * True, for any two Times.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    // Times are strictly comparable with other times
    return openehr_base.Boolean.from(other instanceof DV_TIME);
  }

  /**
   * Value equality comparison.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_TIME)) {
      return openehr_base.Boolean.from(false);
    }
    // Basic implementation: compare values
    return openehr_base.Boolean.from(this.value === other.value);
  }
}

/**
 * Represents an absolute point in time, specified to the second. Semantics defined by ISO 8601.
 *
 * Used for recording a precise point in real world time, and for approximate time stamps, e.g. the origin of a \`HISTORY\` in an \`OBSERVATION\` which is only partially known.
 */
export class DV_DATE_TIME extends DV_TEMPORAL {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * ISO8601 date/time string.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * Numeric value of the date/time as seconds since the calendar origin date/time \`0001-01-01T00:00:00Z\`.
   *
   * @returns Result value
   */
  override ordered_magnitude(): openehr_base.Real {
    // Calculate seconds since 0001-01-01T00:00:00Z
    const val = this.value || "";
    if (!val) {
      return openehr_base.Real.from(0);
    }
    try {
      const dt = new openehr_base.Iso8601_date_time();
      dt.value = val;
      const year = dt.year().value ?? 0;
      const month = dt.month().value ?? 1;
      const day = dt.day().value ?? 1;
      const hour = dt.hour().value ?? 0;
      const minute = dt.minute().value ?? 0;
      const second = dt.second().value;
      const fractional = dt.fractional_second() ?? 0;
      
      // Calculate Julian Day Number for the date part
      const a = Math.floor((14 - month) / 12);
      const y = (year ?? 0) + 4800 - a;
      const m = month + 12 * a - 3;
      
      const jdn = day + Math.floor((153 * m + 2) / 5) + 365 * y +
                  Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
      
      // Origin 0001-01-01 has JDN = 1721426
      const daysSinceOrigin = jdn - 1721426;
      
      // Calculate total seconds
      const secondsInDay = hour * 3600 + minute * 60 + (second ?? 0) + fractional;
      
      return openehr_base.Real.from(daysSinceOrigin * 86400 + secondsInDay);
    } catch {
      return openehr_base.Real.from(0);
    }
  }

  /**
   * Addition of a Duration to this Date/time.
   * Uses the Iso8601_date_time.add() method from BASE package.
   * @param a_diff - Parameter
   * @returns Result value
   */
  override add(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY {
    if (!(a_diff instanceof DV_DURATION)) {
      throw new Error("DV_DATE_TIME.add requires DV_DURATION");
    }
    const dt = new openehr_base.Iso8601_date_time();
    dt.value = this.value || "";
    const dur = new openehr_base.Iso8601_duration();
    dur.value = a_diff.value || "";

    const resultDt = dt.add(dur);

    const result = new DV_DATE_TIME();
    result.value = resultDt.value;
    return result;
  }

  /**
   * Subtract a Duration from this Date/time.
   * Uses the Iso8601_date_time.subtract() method from BASE package.
   * @param a_diff - Parameter
   * @returns Result value
   */
  override subtract(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY {
    if (!(a_diff instanceof DV_DURATION)) {
      throw new Error("DV_DATE_TIME.subtract requires DV_DURATION");
    }
    const dt = new openehr_base.Iso8601_date_time();
    dt.value = this.value || "";
    const dur = new openehr_base.Iso8601_duration();
    dur.value = a_diff.value || "";

    const resultDt = dt.subtract(dur);

    const result = new DV_DATE_TIME();
    result.value = resultDt.value;
    return result;
  }

  /**
   * Difference between this Date/time and \`_other_\`.
   * Uses the Iso8601_date_time.diff() method from BASE package.
   * @param other - Parameter
   * @returns Result value
   */
  override diff(other: DV_ABSOLUTE_QUANTITY): DV_AMOUNT {
    if (!(other instanceof DV_DATE_TIME)) {
      throw new Error("DV_DATE_TIME.diff requires another DV_DATE_TIME");
    }
    const dt1 = new openehr_base.Iso8601_date_time();
    dt1.value = this.value || "";
    const dt2 = new openehr_base.Iso8601_date_time();
    dt2.value = other.value || "";
    
    // Use BASE package diff method
    const resultDur = dt1.diff(dt2);
    
    // Create and return new DV_DURATION with result
    const result = new DV_DURATION();
    result.value = resultDur.value;
    return result;
  }

  /**
   * True if this date-time object is less than \`_other_\`, based on comparison of \`_magnitude()_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_DATE_TIME)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare ISO8601 datetime strings lexicographically (works for standard format)
    if (!this.value || !other.value) {
      return openehr_base.Boolean.from(false);
    }
    return openehr_base.Boolean.from(this.value < other.value);
  }

  /**
   * True, for any two Date/times.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    // Date/times are strictly comparable with other date/times
    return openehr_base.Boolean.from(other instanceof DV_DATE_TIME);
  }

  /**
   * Value equality comparison.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_DATE_TIME)) {
      return openehr_base.Boolean.from(false);
    }
    // Basic implementation: compare values
    return openehr_base.Boolean.from(this.value === other.value);
  }
}
